import { useNavigate } from '@tanstack/react-router';
import { T } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  CaretDownIcon,
  SealCheckIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useEmailTemplateVersions } from '@/api/rpc/hooks/communications/useEmailTemplates';
import { useUsersByIds } from '@/api/core/users/useUsersByIds';
import { useMemo } from 'react';

const menuRowInteractive =
  'cursor-pointer transition-colors duration-150 hover:bg-accent hover:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground';

interface EmailTemplateVersionPickerDropdownProps {
  templateId: string;
  pinnedVersionId: string | null;
  currentVersionNumber: number;
  currentVersionId: string;
  currentStatus: 'draft' | 'finalized';
  currentIsPinned: boolean;
  onOpenFullHistory?: () => void;
}

export function EmailTemplateVersionPickerDropdown({
  templateId,
  pinnedVersionId,
  currentVersionNumber,
  currentVersionId,
  currentStatus,
  currentIsPinned,
  onOpenFullHistory,
}: EmailTemplateVersionPickerDropdownProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useEmailTemplateVersions(templateId);
  const versions = data?.versions ?? [];

  const creatorIds = useMemo(
    () => [...new Set(data?.versions?.map((v) => v.createdBy) ?? [])],
    [data?.versions],
  );
  const { data: users } = useUsersByIds(creatorIds, creatorIds.length > 0);
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    users?.forEach((u) => map.set(u.id, u.name));
    return map;
  }, [users]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <span className="font-mono">
            <T context="version number prefix">v</T>
            {currentVersionNumber}
          </span>
          {currentStatus === 'draft' ? (
            <Badge
              variant="secondary"
              className="h-4 px-1 text-[10px] font-normal"
            >
              <T>Draft</T>
            </Badge>
          ) : currentIsPinned ? (
            <Badge className="h-4 px-1 text-[10px] font-normal">
              <SealCheckIcon className="mr-0.5 h-2.5 w-2.5" weight="fill" />
              <T>Published</T>
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="h-4 px-1 text-[10px] font-normal border-muted-foreground/25 text-muted-foreground"
            >
              <T>Unpublished</T>
            </Badge>
          )}
          <CaretDownIcon className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[360px]">
        <DropdownMenuLabel>
          <T>Jump to version</T>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <CircleNotchIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">
            <T>No versions.</T>
          </div>
        ) : (
          versions.map((v) => {
            const isCurrent = v.id === currentVersionId;
            const isPinned = v.id === pinnedVersionId;
            return (
              <DropdownMenuItem
                key={v.id}
                disabled={isCurrent}
                aria-current={isCurrent ? 'true' : undefined}
                onClick={() => {
                  if (isCurrent) return;
                  if (isPinned) {
                    navigate({
                      to: '/settings/organization/email-templates/$templateId',
                      params: { templateId },
                    });
                  } else {
                    navigate({
                      to: '/settings/organization/email-templates/$templateId/v/$versionNumber',
                      params: {
                        templateId,
                        versionNumber: String(v.version),
                      },
                    });
                  }
                }}
                className={cn(
                  'flex items-center justify-between gap-2',
                  !isCurrent && menuRowInteractive,
                  isCurrent &&
                    'cursor-default bg-muted/50 text-muted-foreground data-[disabled]:opacity-100',
                )}
              >
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="font-mono text-xs font-medium">
                    <T context="version number label prefix">v</T>
                    {v.version}
                  </span>
                  {v.status === 'draft' ? (
                    <Badge
                      variant="secondary"
                      className="h-4 px-1 text-[10px] font-normal"
                    >
                      <T>Draft</T>
                    </Badge>
                  ) : isPinned ? (
                    <Badge className="h-4 px-1 text-[10px] font-normal">
                      <SealCheckIcon
                        className="mr-0.5 h-2.5 w-2.5"
                        weight="fill"
                      />
                      <T>Published</T>
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="h-4 px-1 text-[10px] font-normal"
                    >
                      <T>Finalized</T>
                    </Badge>
                  )}
                  {userMap.get(v.createdBy) && (
                    <span className="ml-auto truncate text-xs text-muted-foreground">
                      {userMap.get(v.createdBy)}
                    </span>
                  )}
                </div>
                {isCurrent ? (
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <T>Viewing</T>
                  </span>
                ) : null}
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        {onOpenFullHistory && (
          <DropdownMenuItem
            onClick={onOpenFullHistory}
            className={menuRowInteractive}
          >
            <T>Open version history…</T>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            navigate({
              to: '/settings/organization/email-templates/$templateId/versions',
              params: { templateId },
            });
          }}
          className={menuRowInteractive}
        >
          <T>View all versions →</T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
